<script setup lang="ts">
import SwiperCore, { EffectFade, Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { SupportTicketCreateModal } from '@/features/supportTickets';
import { ServiceIcon, LocationIcon } from '@glow/shared/components/icons';
import AddressSearchSuggestions from '../AddressSearchSuggestions.vue';
import ServiceSearchSuggestions from '../ServiceSearchSuggestions.vue';
import { useHeroBlockSearch } from '../../composables/useHeroBlockSearch';
import { useRoutes } from '@glow/shared/lib/routes';

SwiperCore.use([EffectFade]);

const props = withDefaults(
  defineProps<{
    variant?: 'client' | 'partner' | 'help';
  }>(),
  { variant: 'client' }
);

const { getUrl } = useRoutes();
const { staticUrl } = useRuntimeConfig().public;
const isSupportModalVisible = ref(false);
const {
  isServiceInputFocused,
  isAddressInputFocused,
  service,
  address,
  serviceError,
  addressError,
  onServiceSelect,
  onAddressSelect,
  isSearching,
  performSearch,
} = useHeroBlockSearch();
</script>

<template>
  <div class="wrap">
    <div class="container">
      <div id="home" class="hero">
        <div class="content">
          <h1 class="title">
            <template v-if="props.variant === 'client'">
              Идеальный мастер
              <br />
              <span>в одном шаге от тебя</span>
            </template>
            <template v-else-if="props.variant === 'partner'">
              <span>Зарабатывай</span>
              на любимом деле
            </template>
            <template v-else-if="props.variant === 'help'">
              <span>Помощь</span>
              в регистрации
            </template>
          </h1>
          <div class="subtitle">
            <template v-if="props.variant === 'client'">
              Свободный мастер с отличными отзывами в 5 минутах, просто укажи
              услугу и адрес
            </template>
            <template v-else-if="props.variant === 'partner'">
              Привлечем и удержим твоих клиентов с помощью наших рекламных
              затрат и инструментов продвижения
            </template>
            <template v-else-if="props.variant === 'help'">
              Твой личный менеджер пройдет все этапы с тобой. Познакомит с
              сервисом и любезно ответит на все вопросы
            </template>
          </div>
          <div class="form">
            <div v-if="props.variant === 'client'" class="inputs">
              <div class="input-wrapper">
                <AppPopover
                  placement="bottom"
                  same-width
                  :padding="0"
                  :distance="8"
                  :model-value="isServiceInputFocused"
                >
                  <template #reference>
                    <AppInput
                      v-model="service"
                      :error="!!serviceError"
                      placeholder="Введите название услуги"
                      variant="white"
                      fluid
                      outlined
                      large
                      static-placeholder
                      @focus="isServiceInputFocused = true"
                      @blur="isServiceInputFocused = false"
                    >
                      <template #prepend>
                        <AppIcon :icon="ServiceIcon" color="accent" />
                      </template>
                      <template #helper-text>
                        {{ serviceError }}
                      </template>
                    </AppInput>
                  </template>
                  <template #popover>
                    <ServiceSearchSuggestions
                      :search-query="service"
                      @select="onServiceSelect"
                    />
                  </template>
                </AppPopover>
              </div>

              <div class="input-wrapper">
                <AppPopover
                  placement="bottom"
                  same-width
                  :padding="0"
                  :distance="8"
                  :model-value="isAddressInputFocused"
                >
                  <template #reference>
                    <AppInput
                      v-model="address"
                      :error="!!addressError"
                      placeholder="Введите адрес"
                      variant="white"
                      fluid
                      outlined
                      large
                      static-placeholder
                      @focus="isAddressInputFocused = true"
                      @blur="isAddressInputFocused = false"
                    >
                      <template #prepend>
                        <AppIcon :icon="LocationIcon" color="accent" />
                      </template>
                      <template #helper-text>
                        {{ addressError }}
                      </template>
                    </AppInput>
                  </template>
                  <template #popover>
                    <AddressSearchSuggestions
                      :search-query="address"
                      @select="onAddressSelect"
                    />
                  </template>
                </AppPopover>
              </div>
            </div>
            <AppButton
              v-if="props.variant === 'client'"
              fluid
              class="target-button"
              :loading="isSearching"
              @click="performSearch"
            >
              НАЙТИ МАСТЕРА
            </AppButton>
            <AppButton
              v-else-if="props.variant === 'partner'"
              fluid
              :to="getUrl('masterRegister')"
              class="target-button"
            >
              Зарегистрироваться
            </AppButton>
            <AppButton
              v-else-if="props.variant === 'help'"
              fluid
              class="target-button"
              @click="isSupportModalVisible = true"
            >
              Мне нужна помощь в регистрации
            </AppButton>
          </div>
        </div>
        <div class="mobile-phone">
          <div class="speaker"></div>
          <Swiper
            :modules="[EffectFade, Autoplay]"
            effect="fade"
            :fade-effect="{ crossFade: true }"
            :loop="true"
            :autoplay="{ delay: 3000 }"
            :no-swiping="true"
            no-swiping-class="swiper-slide"
            class="compact-gallery"
          >
            <SwiperSlide
              v-if="props.variant === 'client'"
              class="compact-gallery__slide"
            >
              <NuxtImg
                format="webp"
                :src="`${staticUrl}/landing/model-woman1.png`"
              />
            </SwiperSlide>
            <SwiperSlide
              v-if="props.variant === 'client'"
              class="compact-gallery__slide"
            >
              <NuxtImg
                format="webp"
                :src="`${staticUrl}/landing/model-man1.png`"
              />
            </SwiperSlide>
            <SwiperSlide
              v-if="props.variant === 'client'"
              class="compact-gallery__slide"
            >
              <NuxtImg
                format="webp"
                :src="`${staticUrl}/landing/model-woman2.png`"
              />
            </SwiperSlide>
            <SwiperSlide
              v-if="props.variant === 'client'"
              class="compact-gallery__slide"
            >
              <NuxtImg
                format="webp"
                :src="`${staticUrl}/landing/model-man2.png`"
              />
            </SwiperSlide>
            <SwiperSlide
              v-if="props.variant === 'client'"
              class="compact-gallery__slide"
            >
              <NuxtImg
                format="webp"
                :src="`${staticUrl}/landing/model-woman3.png`"
              />
            </SwiperSlide>
            <SwiperSlide
              v-if="props.variant === 'client'"
              class="compact-gallery__slide"
            >
              <NuxtImg
                format="webp"
                :src="`${staticUrl}/landing/model-man3.png`"
              />
            </SwiperSlide>
            <SwiperSlide
              v-if="props.variant === 'client'"
              class="compact-gallery__slide"
            >
              <NuxtImg
                format="webp"
                :src="`${staticUrl}/landing/model-woman4.png`"
              />
            </SwiperSlide>

            <SwiperSlide
              v-if="props.variant === 'partner' || props.variant === 'help'"
              class="compact-gallery__slide"
            >
              <NuxtImg
                format="webp"
                :src="`${staticUrl}/landing/master1.png`"
              />
            </SwiperSlide>
            <SwiperSlide
              v-if="props.variant === 'partner' || props.variant === 'help'"
              class="compact-gallery__slide"
            >
              <NuxtImg
                format="webp"
                :src="`${staticUrl}/landing/master2.png`"
              />
            </SwiperSlide>
            <SwiperSlide
              v-if="props.variant === 'partner' || props.variant === 'help'"
              class="compact-gallery__slide"
            >
              <NuxtImg
                format="webp"
                :src="`${staticUrl}/landing/master3.png`"
              />
            </SwiperSlide>
          </Swiper>
        </div>
        <div class="clouds"></div>
      </div>
    </div>

    <SupportTicketCreateModal
      v-model="isSupportModalVisible"
      @submitted="isSupportModalVisible = false"
    />
  </div>
</template>

<style lang="scss" scoped>
.wrap {
  @include media-breakpoint-down(sm) {
    background: var(--app-color-white-secondary);
  }
}

.hero {
  position: relative;
  height: 600px;
  margin-top: 67px;
  padding: 72px 0 0 stretch-size(768, 1920, 30, 100);
  background: var(--app-color-white-secondary);
  border-radius: 24px;

  @include media-breakpoint-down(lg) {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    height: auto;
    padding: 30px 30px 0 30px;
  }

  @include media-breakpoint-down(sm) {
    margin-top: 0;
    border-radius: 0;
    padding: 76px 15px 0 15px;
  }

  .content {
    width: 560px;

    @include media-breakpoint-down(xl) {
      width: 500px;
    }

    @include media-breakpoint-down(lg) {
      width: 100%;
    }

    .title {
      margin-bottom: 27px;
      font-weight: 600;
      font-size: stretch-size(375, 1920, 32, 52);
      line-height: stretch-size(375, 1920, 39, 52);

      span {
        color: var(--app-color-primary);
      }

      @include media-breakpoint-down(lg) {
        width: 100%;
        margin-bottom: 16px;
        text-align: center;
      }
    }

    .subtitle {
      margin-bottom: 20px;
      font-size: stretch-size(375, 1920, 17, 24);
      line-height: stretch-size(375, 1920, 24, 32);

      @include media-breakpoint-down(lg) {
        margin-bottom: 16px;
        text-align: center;
      }
    }

    .form {
      margin-top: 45px;

      @include media-breakpoint-down(lg) {
        margin-bottom: 45px;
      }

      .inputs {
        display: flex;
        flex-direction: column;
        gap: 12px;
        width: 100%;
        margin-bottom: 15px;

        .input-wrapper {
          width: 100%;
        }
      }

      .target-button {
        padding: 1.25em 0;
        font-weight: 500;
      }
    }
  }

  .clouds {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 0;
    width: 310px;
    height: 100%;
    background: url('@/assets/img/landing/camouflage.svg') contain no-repeat;
    border-radius: 0 24px 24px 0;

    @include media-breakpoint-down(lg) {
      display: none;
    }
  }

  .mobile-phone {
    position: absolute;
    right: stretch-size(1200, 1920, 100, 128);
    bottom: 0;
    z-index: 1;
    width: 330px;
    height: 523px;
    padding: 10px 10px 0 10px;
    background: #fff;
    border-radius: 32px 32px 0 0;
    box-shadow:
      1.1125px 1.1125px 3.33749px rgba(80, 88, 113, 0.25),
      4.44999px 4.44999px 11.125px rgba(80, 88, 113, 0.08),
      inset 3.33749px 3.33749px 6.67499px rgba(121, 135, 176, 0.08),
      inset -4.44999px -4.44999px 16.6875px rgba(121, 135, 176, 0.25);

    @include media-breakpoint-down(xl) {
      right: 25px;
    }

    @include media-breakpoint-down(lg) {
      display: none;
    }

    @include media-breakpoint-down(sm) {
      width: 100%;
      height: auto;
    }

    .speaker {
      width: 180px;
      height: 18px;
      position: absolute;
      left: 23%;
      z-index: 2;
      margin-top: -1px;
      background: #fff;
      border-radius: 0 0 12px 12px;

      @include media-breakpoint-down(lg) {
        display: none;
      }
    }
  }
}
</style>

<style lang="scss">
.compact-gallery {
  width: 100%;
  height: 100%;

  &__slide {
    background: linear-gradient(0deg, #d9dae9, #e5e7f6);
    border-radius: 24px 24px 0 0;
    overflow: hidden;

    @include media-breakpoint-down(sm) {
      border-radius: 16px;
    }

    & img {
      width: 100%;
      height: 100%;
      object-fit: cover;

      @include media-breakpoint-down(lg) {
        object-fit: contain;
      }
    }
  }
}
</style>
